import * as React from 'react';
import styled from 'styled-components';
import { createUUID } from '../Services/functions';
import Toast, { ToastOptions } from './Toast';
import { ToastPosition } from '../types';

type ProviderProps = {
  children?: any;
};

interface toast {
  options: ToastOptions;
  id: string;
  content: any;
}
type ToasterValueProps = { toasts: toast[]; addToast: (content: any, options: ToastOptions) => void; removeToast: (id: string) => void };

/*export const ToastContext = React.createContext<ToasterValueProps>(null);*/

export const ToastContext = React.createContext({} as ToasterValueProps);

const ToastBucket = styled.div`
  position: fixed;
  transform: translateX(-50%);
  z-index: 9999;
  display: flex;
  flex-direction: column-reverse;
`;

const ToastBucketTopLeft = styled(ToastBucket)`
  top: 16px;
  left: 5%;
`;

const ToastBucketTopMiddle = styled(ToastBucket)`
  top: 16px;
  left: 50%;
`;

const ToastBucketTopRight = styled(ToastBucket)`
  top: 16px;
  right: 5%;
`;

const ToastBucketBottomLeft = styled(ToastBucket)`
  bottom: 16px;
  left: 5%;
`;

const ToastBucketBottomMiddle = styled(ToastBucket)`
  bottom: 16px;
  left: 50%;
`;

const ToastBucketBottomRight = styled(ToastBucket)`
  bottom: 16px;
  right: 5%;
`;

export const ToastProvider: React.FC = ({ children }: ProviderProps) => {
  const [toasts, setToasts] = React.useState<toast[]>([]);

  const add = (content: any, options: ToastOptions) => {
    const id = createUUID();
    setToasts(prev => [...prev, { id, content, options }]);
  };

  const remove = (id: string) => setToasts(toasts.filter(t => t.id !== id));

  const functions = React.useMemo(() => {
    return { addToast: add, removeToast: remove };
  }, [toasts]);

  return (
    <ToastContext.Provider value={{ toasts, ...functions }}>
      <ToastBucketTopLeft>
        {toasts
          .filter(t => t.options.position === ToastPosition.TOPLEFT)
          .map(t => (
            <Toast key={t.id} content={t.content} remove={() => remove(t.id)} options={t.options} />
          ))}
      </ToastBucketTopLeft>
      <ToastBucketTopMiddle>
        {toasts
          .filter(t => t.options.position === ToastPosition.TOPMIDDLE)
          .map(t => (
            <Toast key={t.id} content={t.content} remove={() => remove(t.id)} options={t.options} />
          ))}
      </ToastBucketTopMiddle>
      <ToastBucketTopRight>
        {toasts
          .filter(t => t.options.position === ToastPosition.TOPRIGHT)
          .map(t => (
            <Toast key={t.id} content={t.content} remove={() => remove(t.id)} options={t.options} />
          ))}
      </ToastBucketTopRight>
      <ToastBucketBottomLeft>
        {toasts
          .filter(t => t.options.position === ToastPosition.BOTTOMLEFT)
          .map(t => (
            <Toast key={t.id} content={t.content} remove={() => remove(t.id)} options={t.options} />
          ))}
      </ToastBucketBottomLeft>
      <ToastBucketBottomMiddle>
        {toasts
          .filter(t => t.options.position === ToastPosition.BOTTOMMIDDLE)
          .map(t => (
            <Toast key={t.id} content={t.content} remove={() => remove(t.id)} options={t.options} />
          ))}
      </ToastBucketBottomMiddle>
      <ToastBucketBottomRight>
        {toasts
          .filter(t => t.options.position === ToastPosition.BOTTOMRIGHT)
          .map(t => (
            <Toast key={t.id} content={t.content} remove={() => remove(t.id)} options={t.options} />
          ))}
      </ToastBucketBottomRight>
      {children}
    </ToastContext.Provider>
  );
};

export function useToast() {
  const { addToast } = React.useContext(ToastContext);
  return {
    addToast,
  };
}
